import { HttpClient, HttpErrorResponse, HttpParams, HttpStatusCode } from '@angular/common/http';
import { Injectable, StateKey, inject, makeStateKey } from '@angular/core';

import { EMPTY, type Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs';

import type {
  CityToCityPairResponse,
  CityToCityResponse,
  CityToCitySuggestions,
  CountryLink,
  Location,
  LocationGeoLocData,
  LocationInputValue,
  LocationMatchData,
  LocationRouteCffData,
  LocationSuggestions,
  SuggestedLocationsResponse,
} from '@fcom/core-api';
import { BUS_CONNECTION_LOCATION_CODES, RAIL_AND_FLY_LOCATION_CODES } from '@fcom/core/constants';
import { retryWithBackoff } from '@fcom/rx';

import { TransportType } from '../../../common-booking/interfaces/utils.interface';
import { ConfigService } from '../config/config.service';
import { MonitoringLogger } from '../monitoring/monitoring-logger';
import { StateTransferService } from '../state-transfer/state-transfer.service';

export enum LocationMatchOnline {
  TRUE = 'true',
  FALSE = 'false',
  ALL = 'all',
}

const endpoints = {
  ROUTE_CFF: '/routecff',
  LOCATION_MATCH: '/locationmatch',
  GEOLOCATION: '/geolocation',
  LOCATION: '/location/',
  SUGGESTIONS: '/suggestedlocations',
  CITY_TO_CITY: '/city-to-city',
  CITY_TO_CITY_PAIR: '/city-to-city-pair',
  COUNTRY_TO_COUNTRY: '/country-to-country',
};

const locationMatchFields = {
  QUERY: 'query',
  LOCALE: 'locale',
  ONLINE: 'online',
  CONTINENT_CODE: 'continentCode',
};

const geolocationMatchFields = {
  LOCALE: 'locale',
  LATITUDE: 'latitude',
  LONGITUDE: 'longitude',
};

enum RouteCffFields {
  ORIGIN = 'originLocation',
  DESTINATION = 'destinationLocation',
  IS_AWARD = 'isAward',
  LOCALE = 'locale',
  IS_CORPORATE = 'isCorporate',
}

enum SuggestedLocationsFields {
  COUNTRY_CODE = 'countryCode',
  LOCALE = 'locale',
}

enum CityToCityFields {
  ORIGIN_COUNTRY_CODE = 'originCountryCode',
  DESTINATION_COUNTRY_CODE = 'destinationCountryCode',
  MARKETING_COUNTRY_CODE = 'marketingCountryCode',
  LOCALE = 'locale',
}

enum CountryToCountryFields {
  LOCALE = 'locale',
  MARKETING_COUNTRY_CODE = 'marketingCountryCode',
}

enum CityToCityPairFields {
  ORIGIN_CITY_CODE = 'originCityCode',
  DESTINATION_CITY_CODE = 'destinationCityCode',
  LOCALE = 'locale',
}

const GERMAN_RAILWAY = 'QYG';
const RETRY_COUNT = 2;

const toSuggestions = <T>(response: {
  departures: { items?: T[] };
  destinations: { items?: T[] };
}): { departures: T[]; destinations: T[] } => {
  return {
    departures: response.departures.items ?? [],
    destinations: response.destinations.items ?? [],
  };
};

@Injectable()
export class LocationRouteCffService {
  static locationStateKey = (query: string, locale: string): StateKey<Location> =>
    makeStateKey<Location>(`loc-${locale}-${query}`);

  private configService = inject(ConfigService);
  private http = inject(HttpClient);
  private monitoringLogger = inject(MonitoringLogger);
  private stateTransferService = inject(StateTransferService);

  private routeCFFUrl: string;
  private locationMatchUrl: string;
  private geolocationMatchUrl: string;
  private singleLocationUrl: string;
  private suggestedLocationsUrl: string;
  private cityToCityLocationsUrl: string;
  private cityToCityPairUrl: string;
  private countryToCountryUrl: string;
  private locationCache: Record<string, Record<string, Location>>;
  private enableGermanRailway: boolean;

  constructor() {
    const configService = this.configService;

    this.routeCFFUrl = configService.cfg.locationRouteCffUrl + endpoints.ROUTE_CFF;
    this.locationMatchUrl = configService.cfg.locationRouteCffUrl + endpoints.LOCATION_MATCH;
    this.geolocationMatchUrl = configService.cfg.locationRouteCffUrl + endpoints.GEOLOCATION;
    this.singleLocationUrl = configService.cfg.locationRouteCffUrl + endpoints.LOCATION;
    this.suggestedLocationsUrl = configService.cfg.locationRouteCffUrl + endpoints.SUGGESTIONS;
    this.cityToCityLocationsUrl = configService.cfg.locationRouteCffUrl + endpoints.CITY_TO_CITY;
    this.cityToCityPairUrl = configService.cfg.locationRouteCffUrl + endpoints.CITY_TO_CITY_PAIR;
    this.countryToCountryUrl = configService.cfg.locationRouteCffUrl + endpoints.COUNTRY_TO_COUNTRY;
    this.enableGermanRailway = this.configService.cfg.enableGermanRailway;
    this.locationCache = {};
  }

  locationMatchesFor(
    query: string,
    locale: string,
    online: LocationMatchOnline = undefined,
    bail = false,
    continentCode?: string
  ): Observable<LocationMatchData> {
    let search: HttpParams = new HttpParams()
      .set(locationMatchFields.QUERY, query)
      .set(locationMatchFields.LOCALE, locale);
    if (online) {
      search = search.set(locationMatchFields.ONLINE, online);
    }

    if (continentCode) {
      search = search.set(locationMatchFields.CONTINENT_CODE, continentCode);
    }

    return this.getLocationMatchesFor(this.locationMatchUrl, search, bail, online);
  }

  bestGuessFor(query: string, locale: string): Observable<Location> {
    if (this.locationCache[locale]?.[query]) {
      return of(this.locationCache[locale][query]);
    } else {
      return this.getLocationWithIataCode(this.singleLocationUrl, query, locale).pipe(
        catchError((error: unknown) => {
          this.monitoringLogger.warn('Could not find exact location match for query', { query, error });
          return this.locationMatchesFor(query, locale, undefined, true).pipe(
            map((l: LocationMatchData) => l.items[0])
          );
        }),
        catchError((error: unknown) => {
          this.monitoringLogger.error('Could not get best guess for query', {
            query,
            error,
          });
          return EMPTY;
        })
      );
    }
  }

  geolocMatchFor(
    latitude: string | number,
    longitude: string | number,
    locale: string
  ): Observable<LocationGeoLocData> {
    const search: HttpParams = new HttpParams()
      .set(geolocationMatchFields.LATITUDE, parseFloat(`${latitude}`).toFixed(1))
      .set(geolocationMatchFields.LONGITUDE, parseFloat(`${longitude}`).toFixed(1))
      .set(geolocationMatchFields.LOCALE, locale);

    return this.getGeolocMatchFor(this.geolocationMatchUrl, search);
  }

  getLocationMatchesFor(
    endpointUrl: string,
    params: HttpParams,
    bail: boolean,
    online: LocationMatchOnline
  ): Observable<LocationMatchData> {
    return this.http.get<LocationMatchData>(endpointUrl, { params }).pipe(
      map((data: LocationMatchData) => {
        // we do not want to accidentally show rail and fly until it is developed
        const locationMatchData = this.enableGermanRailway ? data : this.checkForGermanRailway(data, online);

        // Ensure that LocationMatchData items is an array
        return Array.isArray(locationMatchData.items)
          ? locationMatchData
          : Object.assign({}, locationMatchData, { items: [] });
      }),
      map((data: LocationMatchData) => {
        return {
          ...data,
          items: data.items.map((location) => {
            const transportTypes = [];

            if (BUS_CONNECTION_LOCATION_CODES.includes(location.locationCode)) {
              transportTypes.push(TransportType.Bus);
            }

            if (RAIL_AND_FLY_LOCATION_CODES.includes(location.locationCode)) {
              transportTypes.push(TransportType.Train);
            }

            if (!transportTypes.length) {
              transportTypes.push(TransportType.Flight);
            }

            return {
              ...location,
              transportType: transportTypes,
            };
          }),
        };
      }),
      catchError((err: unknown) => {
        this.monitoringLogger.error('Error getting location match data', {
          error: err,
        });
        const errorResponse: LocationMatchData = {
          ok: false,
          count: 0,
          locale: '',
          items: [],
        };
        return bail ? throwError(() => err) : of(errorResponse);
      })
    );
  }

  checkForGermanRailway(data: LocationMatchData, online: LocationMatchOnline): LocationMatchData {
    if (online !== undefined && online !== LocationMatchOnline.TRUE) {
      return data;
    }

    return {
      ...data,
      items: data.items?.filter((location) => location.locationCode !== GERMAN_RAILWAY),
    };
  }

  getGeolocMatchFor(endpointUrl: string, params: HttpParams): Observable<LocationGeoLocData> {
    return this.http.get<LocationGeoLocData>(endpointUrl, { params }).pipe(
      catchError((err: unknown) => {
        this.monitoringLogger.error('Error getting geoloc data', { error: err });
        const errorResponse: LocationGeoLocData = {
          ok: false,
          locale: '',
          item: undefined,
        };
        return of(errorResponse);
      })
    );
  }

  routeCffsFor(
    departureLocation: string,
    destinationLocation: string,
    isAward?: string,
    locale?: string,
    isCorporate?: string
  ): Observable<LocationRouteCffData> {
    let search: HttpParams = new HttpParams()
      .set(RouteCffFields.ORIGIN, departureLocation)
      .set(RouteCffFields.DESTINATION, destinationLocation);

    if (isAward) {
      search = search.set(RouteCffFields.IS_AWARD, isAward);
    }

    if (locale) {
      search = search.set(RouteCffFields.LOCALE, locale);
    }

    if (isCorporate) {
      search = search.set(RouteCffFields.IS_CORPORATE, isCorporate);
    }

    return this.getRouteCffsFor(this.routeCFFUrl, search);
  }

  getSuggestedLocations(countryCode: string, locale: string): Observable<LocationSuggestions> {
    const params: HttpParams = new HttpParams()
      .set(SuggestedLocationsFields.COUNTRY_CODE, countryCode)
      .set(SuggestedLocationsFields.LOCALE, locale);
    return this.withLocationSuggestionsFallback(
      this.http.get<SuggestedLocationsResponse>(this.suggestedLocationsUrl, { params }).pipe(map(toSuggestions))
    );
  }

  /**
   * Fetches city-to-city location suggestions.
   *
   * When `originCountryCode` is provided, returns departures and destinations for a specific country
   * pair (city-to-city cross-linking use case).
   *
   * When `originCountryCode` is omitted, returns a network-wide list of notable destination cities
   * ranked by market country first (network listing use case, e.g. `/flights/from`). In this mode
   * `marketingCountryCode` controls the ranking. The backend decides the number of results.
   */
  getCityToCityLocations(
    originCountryCode?: string,
    destinationCountryCode?: string,
    locale?: string,
    marketingCountryCode?: string
  ): Observable<CityToCitySuggestions> {
    return this.withLocationSuggestionsFallback(
      this.requestCityToCityLocations(originCountryCode, destinationCountryCode, locale, marketingCountryCode)
    );
  }

  requestCityToCityLocations(
    originCountryCode?: string,
    destinationCountryCode?: string,
    locale?: string,
    marketingCountryCode?: string
  ): Observable<CityToCitySuggestions> {
    let params = new HttpParams();

    if (originCountryCode) {
      params = params.set(CityToCityFields.ORIGIN_COUNTRY_CODE, originCountryCode);

      if (destinationCountryCode) {
        params = params.set(CityToCityFields.DESTINATION_COUNTRY_CODE, destinationCountryCode);
      }
    } else {
      if (marketingCountryCode) {
        params = params.set(CityToCityFields.MARKETING_COUNTRY_CODE, marketingCountryCode);
      }
    }

    if (locale) {
      params = params.set(CityToCityFields.LOCALE, locale);
    }

    return this.http.get<CityToCityResponse>(this.cityToCityLocationsUrl, { params }).pipe(map(toSuggestions));
  }

  getCountryToCountry(locale?: string, marketingCountryCode?: string): Observable<CountryLink[]> {
    let params = new HttpParams();

    if (locale) {
      params = params.set(CountryToCountryFields.LOCALE, locale);
    }

    if (marketingCountryCode) {
      params = params.set(CountryToCountryFields.MARKETING_COUNTRY_CODE, marketingCountryCode);
    }

    return this.http.get<CountryLink[]>(this.countryToCountryUrl, { params }).pipe(
      catchError((error: unknown) => {
        this.monitoringLogger.error('Error getting country-to-country links', { error });
        return of([]);
      })
    );
  }

  getCityToCityPair(
    originCityCode: string,
    destinationCityCode: string,
    locale: string
  ): Observable<CityToCityPairResponse | null> {
    const params = new HttpParams()
      .set(CityToCityPairFields.ORIGIN_CITY_CODE, originCityCode)
      .set(CityToCityPairFields.DESTINATION_CITY_CODE, destinationCityCode)
      .set(CityToCityPairFields.LOCALE, locale);

    return this.stateTransferService
      .wrapToStateCache(
        makeStateKey<CityToCityPairResponse | null>(`c2c-pair-${locale}-${originCityCode}-${destinationCityCode}`),
        () => {
          return this.http.get<CityToCityPairResponse>(this.cityToCityPairUrl, { params, transferCache: false }).pipe(
            retryWithBackoff(RETRY_COUNT),
            catchError((error: unknown) => {
              if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.NotFound) {
                return of(null);
              }
              return throwError(() => error);
            })
          );
        }
      )
      .pipe(
        catchError((error: unknown) => {
          this.monitoringLogger.error('Error getting city-to-city pair faqs', { error });
          return of(null);
        })
      );
  }

  withLocationSuggestionsFallback<T extends { departures: unknown[]; destinations: unknown[] }>(
    suggestions$: Observable<T>
  ): Observable<T | { departures: never[]; destinations: never[] }> {
    return suggestions$.pipe(
      catchError((error: unknown) => {
        this.monitoringLogger.error('Error getting location suggestions', { error });
        return of<{ departures: never[]; destinations: never[] }>({ departures: [], destinations: [] });
      })
    );
  }

  getLocaleCityName(query: string, targetLocale: string): Observable<string> {
    if (this.locationCache[targetLocale]?.[query]?.cityName) {
      return of(this.locationCache[targetLocale][query].cityName);
    } else {
      return this.getLocationWithIataCode(this.singleLocationUrl, query, targetLocale).pipe(
        map((locationData: Location): string => locationData?.cityName),
        catchError((err: unknown) => {
          this.monitoringLogger.warn('Error finding LocaleCityName', { query, error: err });
          return throwError(() => err);
        })
      );
    }
  }

  getQueryString(query: LocationInputValue): string {
    if (!query || 'string' !== typeof query) {
      return '';
    } else if (query.startsWith('(')) {
      return query.split(')').pop().trim();
    }
    return query.trim();
  }

  private getLocationWithIataCode(endpointUrl: string, query: string, locale: string): Observable<Location> {
    return this.stateTransferService
      .wrapToStateCache(LocationRouteCffService.locationStateKey(query, locale), () =>
        this.http.get(endpointUrl + query, { transferCache: false }).pipe(
          map((exactLocationData: Record<string, unknown>): Location => {
            const title = (exactLocationData[`title_${locale}`] || exactLocationData['title']) as string;
            const country = (exactLocationData[`country_${locale}`] || exactLocationData['country']) as string;
            const cityName = (exactLocationData[`cityName_${locale}`] || exactLocationData['cityName']) as string;
            const location: Location = {
              title,
              country,
              cityName,
              countryCode: exactLocationData.countryCode as string,
              locationCode: exactLocationData.locationCode as string,
              AJBPartner: exactLocationData.ajbPartner as boolean,
              SJBPartner: exactLocationData.sjbPartner as boolean,
              airAlliance: exactLocationData.airAlliance as boolean,
              airAward: exactLocationData.airAward as boolean,
              airPartner: exactLocationData.airPartner as boolean,
              latitude: exactLocationData.latitude as number,
              longitude: exactLocationData.longitude as number,
              type: exactLocationData.type as Location['type'],
              picture: exactLocationData.picture as Location['picture'],
              continentCode: exactLocationData.continentCode as string,
            };
            if (exactLocationData.locationCityCode) {
              location.locationCityCode = exactLocationData.locationCityCode as string;
            }

            return location;
          }),
          catchError((err: unknown) => {
            this.monitoringLogger.warn('Error finding location', { query, error: err });
            return throwError(() => err);
          })
        )
      )
      .pipe(
        tap((location: Location) => {
          if (location) {
            if (!this.locationCache[locale]) {
              this.locationCache[locale] = {};
            }
            this.locationCache[locale][query] = location;
          }
        })
      );
  }

  private getRouteCffsFor(endpointUrl: string, params: HttpParams): Observable<LocationRouteCffData> {
    return this.http.get<LocationRouteCffData>(endpointUrl, { params }).pipe(
      catchError((err: unknown) => {
        this.monitoringLogger.error('Error getting route cff data', { error: err });
        return of({} as LocationRouteCffData);
      })
    );
  }
}
